import { Head, Link, useForm } from "@inertiajs/react"
import { format } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Plus,
  Target,
  TrendingUp,
  Calendar,
  MoreHorizontal,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { goalsPath, newGoalPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Goals",
    href: goalsPath(),
  },
]

interface Goal {
  id: number
  target_amount: number
  current_amount: number
  goal_type: string
  target_date: string | null
  created_at: string
  updated_at: string
  progress_percentage: number
  amount_remaining: number
  goal_type_human: string
  completed?: boolean
  months_to_completion?: number
}

interface GoalsIndexProps {
  goals: Goal[]
  summary: {
    total_goals: number
    completed_goals: number
    total_target_amount: number
    total_current_amount: number
    average_progress: number
  }
}

export default function GoalsIndex({ goals, summary }: GoalsIndexProps) {
  const { delete: destroy } = useForm()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy")
  }

  const getProgressColor = (progress: number) => {
    if (progress >= 100) return "text-green-600"
    if (progress >= 75) return "text-blue-600"
    if (progress >= 50) return "text-yellow-600"
    return "text-red-600"
  }

  const getStatusBadge = (goal: Goal) => {
    if (goal.completed) {
      return (
        <Badge className="border-green-300 bg-green-100 text-green-800">
          <CheckCircle className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    }

    if (goal.progress_percentage >= 75) {
      return (
        <Badge className="border-blue-300 bg-blue-100 text-blue-800">
          <TrendingUp className="mr-1 h-3 w-3" />
          On Track
        </Badge>
      )
    }

    if (goal.progress_percentage >= 50) {
      return (
        <Badge className="border-yellow-300 bg-yellow-100 text-yellow-800">
          <Clock className="mr-1 h-3 w-3" />
          Progressing
        </Badge>
      )
    }

    return (
      <Badge className="border-red-300 bg-red-100 text-red-800">
        <AlertCircle className="mr-1 h-3 w-3" />
        Behind
      </Badge>
    )
  }

  return (
    <AppLayout>
      <Head title="Goals" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Financial Goals</h1>
            <p className="text-muted-foreground">
              Track your progress toward financial independence
            </p>
          </div>
          <Button asChild>
            <Link href={newGoalPath()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Goal
            </Link>
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Goals</CardTitle>
              <Target className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.total_goals}</div>
              <p className="text-muted-foreground text-xs">
                {summary.completed_goals} completed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Target
              </CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.total_target_amount)}
              </div>
              <p className="text-muted-foreground text-xs">Across all goals</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Current Saved
              </CardTitle>
              <CheckCircle className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.total_current_amount)}
              </div>
              <p className="text-muted-foreground text-xs">
                {(
                  (summary.total_current_amount / summary.total_target_amount) *
                  100
                ).toFixed(1)}
                % of target
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Avg Progress
              </CardTitle>
              <Calendar className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {(summary.average_progress || 0).toFixed(1)}%
              </div>
              <Progress
                value={summary.average_progress || 0}
                className="mt-2"
              />
            </CardContent>
          </Card>
        </div>

        {/* Goals Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {goals.length === 0 ? (
            <div className="col-span-full">
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Target className="text-muted-foreground mb-4 h-16 w-16" />
                  <h3 className="mb-2 text-lg font-semibold">No goals yet</h3>
                  <p className="text-muted-foreground mb-6 text-center">
                    Create your first financial goal to start tracking your
                    progress toward financial independence.
                  </p>
                  <Button asChild>
                    <Link href={newGoalPath()}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Your First Goal
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : (
            goals.map((goal) => (
              <Card key={goal.id} className="relative">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {goal.goal_type_human}
                      </CardTitle>
                      <div className="mt-2 flex items-center gap-2">
                        {getStatusBadge(goal)}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link href={`/goals/${goal.id}/edit`}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => {
                            if (
                              confirm(
                                "Are you sure you want to delete this goal?",
                              )
                            ) {
                              destroy(`/goals/${goal.id}`)
                            }
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress */}
                  <div>
                    <div className="mb-2 flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span
                        className={`font-medium ${getProgressColor(goal.progress_percentage)}`}
                      >
                        {goal.progress_percentage.toFixed(1)}%
                      </span>
                    </div>
                    <Progress
                      value={goal.progress_percentage}
                      className="h-2"
                    />
                  </div>

                  {/* Amounts */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Current</div>
                      <div className="font-semibold">
                        {formatCurrency(goal.current_amount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Target</div>
                      <div className="font-semibold">
                        {formatCurrency(goal.target_amount)}
                      </div>
                    </div>
                  </div>

                  {/* Remaining */}
                  <div>
                    <div className="text-muted-foreground mb-1 text-sm">
                      Remaining
                    </div>
                    <div className="text-lg font-semibold">
                      {formatCurrency(goal.amount_remaining)}
                    </div>
                  </div>

                  {/* Target Date */}
                  {goal.target_date && (
                    <div>
                      <div className="text-muted-foreground mb-1 text-sm">
                        Target Date
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="text-muted-foreground h-4 w-4" />
                        <span className="text-sm">
                          {formatDate(goal.target_date)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Time to Completion */}
                  {goal.months_to_completion &&
                    goal.months_to_completion > 0 && (
                      <div>
                        <div className="text-muted-foreground mb-1 text-sm">
                          Est. Completion
                        </div>
                        <div className="text-sm">
                          {goal.months_to_completion} months
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AppLayout>
  )
}
